/** Metrics derived from referenced report logs (balance pies + histogram, or counts fallback). */

export type ReportLogMetricSource = {
  type: string,
  message?: string,
  value?: string,
  units?: string,
};

/** @deprecated Always balances-first — kept for older saved report payloads. */
export type ReportChartsMetricMode = 'counts' | 'balances';

export type ReportMetricChart = {
  id: string,
  title: string,
  kind: 'doughnut' | 'bar' | 'line',
  labels: string[],
  values: number[],
  /** Optional currency-style formatting for values / center total. */
  formatAsMoney?: boolean,
  /** Net balance shown in the doughnut hole (pies only). */
  centerTotal?: number,
  /** Short label under the center total. */
  centerLabel?: string,
};

export type ReportMetricsSummary = {
  logCount: number,
  /** How many referenced logs had a parseable numeric value. */
  valueLogCount: number,
  /** What the carousel header count refers to. */
  headerCountKind: 'balances' | 'logs',
  charts: ReportMetricChart[],
  /**
   * Only set when charts cannot be built (e.g. fewer than 2 activityLogs).
   * Partial-balance notes are not used — the header shows the balance count instead.
   */
  statusNote: string | null,
};

const BLUE_PALETTE = [
  '#2563eb', '#3b82f6', '#60a5fa', '#1d4ed8', '#93c5fd',
  '#0ea5e9', '#0284c8', '#38bdf8', '#0369a1', '#7dd3fc',
];

export const reportMetricColors = (n: number): string[] => {
  if (n <= 0) return [];
  const out: string[] = [];
  for (let i = 0; i < n; i += 1) {
    out.push(BLUE_PALETTE[i % BLUE_PALETTE.length]);
  }
  return out;
};

export const parseReportLogMoney = (raw?: string): number | null => {
  if (raw == null || raw === '' || raw === 'N/A') return null;
  const cleaned = String(raw).replace(/[^0-9.-]/g, '');
  if (!cleaned || cleaned === '-' || cleaned === '.') return null;
  const n = Number(cleaned);
  return Number.isFinite(n) ? n : null;
};

/** Amount used for metrics — units column when /logs header is Units. */
const metricAmountFromLog = (
  log: ReportLogMetricSource,
  valueMode: 'value' | 'units',
): number | null => (
  valueMode === 'units'
    ? parseReportLogMoney(log.units)
    : parseReportLogMoney(log.value)
);

/** Format center / tooltip numbers for chart overlays. */
export const formatReportMetricTotal = (value: number, asMoney?: boolean): string => {
  if (!Number.isFinite(value)) return '—';
  if (asMoney) {
    return value.toLocaleString(undefined, {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    });
  }
  return String(Math.round(value));
};

/**
 * Pie center figure color — green/red contrast against blue slices.
 * Zero stays dark so it does not look like a gain.
 */
export const reportPieFigureColor = (value: number): string => {
  if (!Number.isFinite(value) || value === 0) return '#1c1c1c';
  return value < 0 ? '#dc2626' : '#16a34a';
};

/** Max named slices on a pie/doughnut; the rest become one "Others" slice. */
export const REPORT_PIE_MAX_SLICES = 5;
/** Pie legend / slice names stay short so the legend fits. */
export const REPORT_PIE_LABEL_MAX_CHARS = 12;

/** Truncate a pie slice name to REPORT_PIE_LABEL_MAX_CHARS (ellipsis when clipped). */
export const truncatePieSliceLabel = (
  label: string,
  maxChars: number = REPORT_PIE_LABEL_MAX_CHARS,
): string => {
  const t = String(label || '').trim();
  if (t.length <= maxChars) return t;
  return `${t.slice(0, Math.max(1, maxChars - 1))}…`;
};

/**
 * Keep the top N slices by value; fold the remainder into a single "Others" slice.
 * Used for doughnut/pie previews so legends stay readable.
 */
export const collapsePieSlices = (
  labels: string[],
  values: number[],
  maxSlices: number = REPORT_PIE_MAX_SLICES,
): { labels: string[], values: number[] } => {
  const pairs = labels.map((label, i) => ({
    label: String(label || '').trim() || 'Activity',
    value: Number(values[i]) || 0,
  }));
  let nextLabels: string[];
  let nextValues: number[];
  if (pairs.length <= maxSlices) {
    nextLabels = pairs.map((p) => p.label);
    nextValues = pairs.map((p) => p.value);
  } else {
    // Largest slices keep their own wedge; smaller ones roll into Others.
    pairs.sort((a, b) => b.value - a.value || a.label.localeCompare(b.label));
    const top = pairs.slice(0, maxSlices);
    const rest = pairs.slice(maxSlices);
    const othersTotal = rest.reduce((sum, p) => sum + p.value, 0);
    nextLabels = top.map((p) => p.label);
    nextValues = top.map((p) => p.value);
    if (othersTotal > 0 || rest.length) {
      nextLabels.push('Others');
      nextValues.push(othersTotal);
    }
  }
  return {
    // Full names for tooltips; legend truncates in the chart UI.
    labels: nextLabels,
    values: nextValues,
  };
};

/** Fixed titles for the SKU bar / count charts (not editable). */
export const defaultReportBalanceChartTitle = (
  valueMode: 'value' | 'units' = 'value',
): string => (valueMode === 'units' ? 'Units by SKU' : 'Balance by SKU');

/** Dark set label in the chart header — empty chartsTitle falls back to this. */
export const defaultReportActivitySetChartsLabel = (serial: number): string => (
  `Activity ${Math.max(1, Math.floor(Number(serial) || 1))}`
);

/** Count pie for sets with no numeric values (non-balance logs). */
const buildCountCharts = (logs: ReportLogMetricSource[]): ReportMetricChart[] => {
  const countByType = new Map<string, number>();
  logs.forEach((log) => {
    const type = String(log.type || 'Activity').trim() || 'Activity';
    countByType.set(type, (countByType.get(type) || 0) + 1);
  });
  const countLabels = [...countByType.keys()];
  const countCollapsed = collapsePieSlices(
    countLabels,
    countLabels.map((k) => countByType.get(k) || 0),
  );
  const total = countCollapsed.values.reduce((s, v) => s + v, 0);
  return [{
    id: 'count_by_type',
    title: 'Log count by SKU',
    kind: 'doughnut',
    labels: countCollapsed.labels,
    values: countCollapsed.values,
    // No money center — header shows log count instead.
    centerTotal: total,
    centerLabel: 'Total',
  }];
};

/**
 * Build chart specs when more than one activity is selected.
 * Balances: histogram → cash-in/out pie (no balance-by-SKU pie/line —
 * signed type totals don't fit a pie, and the line slot stays empty).
 * Fall back to count pie when no amounts. Mixed sets also get a count pie
 * for the N/A (non-balance) logs so they stay in the preview.
 * valueMode units → use frozen units field (no money formatting).
 */
export const buildReportMetrics = (
  logs: ReportLogMetricSource[],
  _mode: ReportChartsMetricMode = 'balances',
  valueMode: 'value' | 'units' = 'value',
): ReportMetricsSummary => {
  const logCount = logs.length;
  const asMoney = valueMode !== 'units';
  let valueLogCount = 0;
  logs.forEach((log) => {
    if (metricAmountFromLog(log, valueMode) != null) valueLogCount += 1;
  });

  if (logCount === 0) {
    return {
      logCount: 0,
      valueLogCount: 0,
      headerCountKind: 'logs',
      charts: [],
      statusNote: null,
    };
  }

  if (logCount === 1) {
    return {
      logCount,
      valueLogCount,
      headerCountKind: valueLogCount > 0 ? 'balances' : 'logs',
      charts: [],
      statusNote: null,
    };
  }

  // No amounts → count charts so non-balance logs still get a preview.
  if (valueLogCount === 0) {
    return {
      logCount,
      valueLogCount: 0,
      headerCountKind: 'logs',
      charts: buildCountCharts(logs),
      statusNote: null,
    };
  }

  const sumByType = new Map<string, number>();
  let cashIn = 0;
  let cashOut = 0;
  let netBalance = 0;

  logs.forEach((log) => {
    const type = String(log.type || 'Activity').trim() || 'Activity';
    const amount = metricAmountFromLog(log, valueMode);
    if (amount == null) return;
    sumByType.set(type, (sumByType.get(type) || 0) + amount);
    netBalance += amount;
    if (amount >= 0) cashIn += amount;
    else cashOut += Math.abs(amount);
  });

  const charts: ReportMetricChart[] = [];
  const balLabels = [...sumByType.keys()];
  const balSigned = balLabels.map((k) => sumByType.get(k) || 0);

  // Signed histogram — fixed Balance/Units by SKU title.
  charts.push({
    id: 'balance_by_type',
    title: defaultReportBalanceChartTitle(valueMode),
    kind: 'bar',
    labels: balLabels,
    values: balSigned,
    formatAsMoney: asMoney,
  });

  // Cash/units pie — fixed title.
  if (cashIn > 0 || cashOut > 0) {
    const cashCollapsed = collapsePieSlices(
      valueMode === 'units' ? ['Units in', 'Units out'] : ['Cash in', 'Cash out'],
      [cashIn, cashOut],
    );
    charts.push({
      id: 'cash_in_out',
      title: valueMode === 'units' ? 'Units in vs out' : 'Cash in vs out',
      kind: 'doughnut',
      labels: cashCollapsed.labels,
      values: cashCollapsed.values,
      formatAsMoney: asMoney,
      centerTotal: netBalance,
      centerLabel: valueMode === 'units' ? 'Net' : 'Balance',
    });
  }

  // Mixed set: also chart N/A logs when there are enough to plot.
  const nonBalanceLogs = logs.filter((log) => metricAmountFromLog(log, valueMode) == null);
  if (nonBalanceLogs.length > 1) {
    charts.push(...buildCountCharts(nonBalanceLogs).map((chart) => ({
      ...chart,
      id: 'count_non_balance_by_type',
      title: 'Non-balance logs by SKU',
    })));
  }

  return {
    logCount,
    valueLogCount,
    headerCountKind: 'balances',
    charts,
    statusNote: null,
  };
};
